export const getJob = `
  query GetJob($jobId: ID!, $tenantId: String!) {
    getJob(jobId: $jobId, tenantId: $tenantId) {
      jobId
      name
      tenantId
      username
      cognitoSub
      jobType
      status
      documentProcessingStatus
      documentVerificationStatus
      investorGuidelineReviewStatus
      loanCount
      processedLoanCount
      failedLoanCount
      verifiedDocumentCount
      totalDocumentCount
      guidelineCheckCount
      totalGuidelineCount
      uploadLocation
      exportLocation
      createdAt
      startedAt
      completedAt
      # Title Search (jobType === 'TR') fields - nullable, populated only for TR jobs
      loanNumber
      clientName
      borrowerName
      effectiveDate
      propertyAddress
      propertyCounty
      propertyState
      propertyTaxSearchStatus
      recordedDocumentSearchStatus
      findingsDisplayStatus
      # Title Analysis fields - nullable, populated only for TR jobs
      useCase
      vestingEntity
      guarantorNames
      subjectLoanAmount
      noteDate
      searchStartDate
      indexesSearched
      subjectMortgageRecordingReference
      expectedLienPosition
      currentLoanHolder
      apn
      titleAnalysisStatus
      titleAnalysisError
      propertyTaxInformation {
        taxSaleStatus
        taxYear
        taxType
        taxId
        taxingEntity
        totalAnnualTax
        paymentFrequency
        installments {
          status
          estimated
          amount
          dueDate
          delinquentDate
          paidDate
        }
        billYearNotes
        trueTaxAmount
      }
      deedInformation {
        deedType
        grantee
        grantor
        deedDate
        recordedDate
        bookPageOrInstrument
        considerationAmount
      }
    }
  }
`;

export const getLoan = `
  query GetLoan($loanId: ID!, $jobId: ID!, $tenantId: String!) {
    getLoan(loanId: $loanId, jobId: $jobId, tenantId: $tenantId) {
      loanId
      jobId
      tenantId
      username
      cognitoSub
      loanNumber
      borrowerName
      propertyAddress
      loanAmount
      coBorrowerNames
      fileCount
      status
      createdAt
      startedAt
      completedAt
      processedFileCount
      failedFileCount
    }
  }
`;

export const listJobsByUser = `
  query ListJobsByUser($tenantId: String!, $username: String!, $jobType: JobType!, $limit: Int, $nextToken: String, $archivedOnly: Boolean) {
    listJobsByUser(tenantId: $tenantId, username: $username, jobType: $jobType, limit: $limit, nextToken: $nextToken, archivedOnly: $archivedOnly) {
      items {
        jobId
        name
        tenantId
        username
        cognitoSub
        jobType
        status
        documentProcessingStatus
        documentVerificationStatus
        investorGuidelineReviewStatus
        loanCount
        processedLoanCount
        failedLoanCount
        verifiedDocumentCount
        totalDocumentCount
        guidelineCheckCount
        totalGuidelineCount
        uploadLocation
        exportLocation
        createdAt
        startedAt
        completedAt
      }
      nextToken
    }
  }
`;

export const listLoansByJob = `
  query ListLoansByJob($jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listLoansByJob(jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        loanId
        jobId
        tenantId
        username
        cognitoSub
        loanNumber
        borrowerName
        propertyAddress
        loanAmount
        fileCount
        status
        createdAt
        startedAt
        completedAt
        processedFileCount
        failedFileCount
      }
      nextToken
    }
  }
`;

export const listFilesByLoan = `
  query ListFilesByLoan($loanId: ID!, $jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listFilesByLoan(loanId: $loanId, jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        fileId
        loanId
        jobId
        tenantId
        username
        cognitoSub
        filename
        fileSize
        s3Key
        status
        pageCount
        splitDocumentCount
        invocationId
        createdAt
        processedAt
        errorMessage
      }
      nextToken
    }
  }
`;

export const listFilesByJob = `
  query ListFilesByJob($jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listFilesByJob(jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        fileId
        loanId
        jobId
        tenantId
        username
        cognitoSub
        filename
        fileSize
        s3Key
        status
        pageCount
        splitDocumentCount
        invocationId
        createdAt
        processedAt
        errorMessage
      }
      nextToken
    }
  }
`;

export const listDocumentsByFile = `
  query ListDocumentsByFile($fileId: ID!, $loanId: ID!, $jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listDocumentsByFile(fileId: $fileId, loanId: $loanId, jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        documentId
        fileId
        loanId
        jobId
        tenantId
        username
        cognitoSub
        sourceFilename
        pageRange
        documentType
        confidenceScore
        requiresHumanReview
        processedDataS3Key
        createdAt
        processedAt
        reviewTaskARN
        errorMessage
        extractedFields
      }
      nextToken
    }
  }
`;

export const listDocumentsByLoan = `
  query ListDocumentsByLoan($loanId: ID!, $jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listDocumentsByLoan(loanId: $loanId, jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        documentId
        fileId
        loanId
        jobId
        tenantId
        username
        cognitoSub
        sourceFilename
        pageRange
        documentType
        confidenceScore
        requiresHumanReview
        processedDataS3Key
        createdAt
        processedAt
        reviewTaskARN
        errorMessage
        extractedFields
      }
      nextToken
    }
  }
`;

export const listDocumentsByJob = `
  query ListDocumentsByJob($jobId: ID!, $tenantId: String!, $limit: Int, $nextToken: String) {
    listDocumentsByJob(jobId: $jobId, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        documentId
        fileId
        loanId
        jobId
        tenantId
        username
        cognitoSub
        sourceFilename
        pageRange
        documentType
        confidenceScore
        requiresHumanReview
        processedDataS3Key
        createdAt
        processedAt
        reviewTaskARN
        errorMessage
        extractedFields
      }
      nextToken
    }
  }
`;

export const getDocumentSplitUrl = `
  query GetDocumentSplitUrl($documentId: ID!, $jobId: ID!) {
    getDocumentSplitUrl(documentId: $documentId, jobId: $jobId)
  }
`;

export const listGuidelines = `
  query ListGuidelines($tenantId: String!, $type: String) {
    listGuidelines(tenantId: $tenantId, type: $type) {
      tenantId
      guidelineId
      name
      description
      type
    }
  }
`;

export const getGuidelineReviewResults = `
  query GetGuidelineReviewResults($jobId: ID!, $tenantId: String!, $guidelineId: ID) {
    getGuidelineReviewResults(jobId: $jobId, tenantId: $tenantId, guidelineId: $guidelineId) {
      findings {
        loanId
        status
        explanation
        unmetCriteria
      }
    }
  }
`;

export const getDocumentVerificationResults = `
  query GetDocumentVerificationResults($jobId: ID!, $tenantId: String!, $guidelineId: ID!) {
    getDocumentVerificationResults(jobId: $jobId, tenantId: $tenantId, guidelineId: $guidelineId) {
      jobId
      tenantId
      results {
        documentName
        status
        reason
      }
    }
  }
`;

export const getConsistencyVerificationResults = `
  query GetConsistencyVerificationResults($jobId: ID!, $tenantId: String!, $guidelineId: ID!) {
    getConsistencyVerificationResults(jobId: $jobId, tenantId: $tenantId, guidelineId: $guidelineId) {
      jobId
      tenantId
      results {
        checkName
        status
        reason
      }
    }
  }
`;

export const getLoanBookmarkedPdfUrl = `
  query GetLoanBookmarkedPdfUrl($jobId: ID!, $loanId: ID!) {
    getLoanBookmarkedPdfUrl(jobId: $jobId, loanId: $loanId)
  }
`;
